import { useState } from "react";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import Modal from "@/components/base/Modal";
import {
  useAllPayments,
  confirmHavalePayment,
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_COLORS,
  PAYMENT_METHOD_LABELS,
} from "@/hooks/usePayments";

function formatPrice(value: number): string {
  return `₺${value.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function AdminOdemeler() {
  const { data, loading, error, reload } = useAllPayments();
  const list = data ?? [];
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const pendingHavale = list.filter((p) => p.method === "havale" && p.status === "pending").length;
  const totalPaid = list.filter((p) => p.status === "paid").reduce((s, p) => s + p.amount, 0);

  const handleConfirm = async () => {
    if (!confirming) return;
    setBusy(true);
    try {
      await confirmHavalePayment(confirming);
      setConfirming(null);
      reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Ödemeler"
        description="Öğrencilerin ödeme ve abonelik işlemlerini görüntüle ve onayla."
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <p className="text-xs font-medium text-foreground-500">Bekleyen havale</p>
          <p className="mt-2 font-heading font-bold text-2xl text-secondary-700">{pendingHavale}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <p className="text-xs font-medium text-foreground-500">Toplam tahsilat</p>
          <p className="mt-2 font-heading font-bold text-2xl text-accent-700">{formatPrice(totalPaid)}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <p className="text-xs font-medium text-foreground-500">Toplam işlem</p>
          <p className="mt-2 font-heading font-bold text-2xl text-foreground-950">{list.length}</p>
        </div>
      </div>

      {loading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : list.length === 0 ? (
        <EmptyState
          icon="ri-bank-card-line"
          title="Henüz ödeme yok"
          description="Öğrenciler paket satın aldığında ödemeler burada listelenecek."
        />
      ) : (
        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/60">
                  <th className="px-4 py-3 text-left font-semibold text-foreground-700">Kullanıcı</th>
                  <th className="px-4 py-3 text-left font-semibold text-foreground-700">Paket</th>
                  <th className="px-4 py-3 text-left font-semibold text-foreground-700">Tutar</th>
                  <th className="px-4 py-3 text-left font-semibold text-foreground-700">Yöntem</th>
                  <th className="px-4 py-3 text-left font-semibold text-foreground-700">Tarih</th>
                  <th className="px-4 py-3 text-left font-semibold text-foreground-700">Durum</th>
                  <th className="px-4 py-3 text-right font-semibold text-foreground-700">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {list.map((p) => (
                  <tr key={p.id} className="border-b border-background-200 hover:bg-background-100/40 transition-colors">
                    <td className="px-4 py-3 font-medium text-foreground-950">{p.userName}</td>
                    <td className="px-4 py-3 text-foreground-700">{p.packageName || "—"}</td>
                    <td className="px-4 py-3 text-foreground-900 font-semibold whitespace-nowrap">{formatPrice(p.amount)}</td>
                    <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">
                      {PAYMENT_METHOD_LABELS[p.method] ?? p.method}
                    </td>
                    <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{p.createdAt}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${PAYMENT_STATUS_COLORS[p.status] ?? "bg-background-100 text-foreground-600"}`}>
                        {PAYMENT_STATUS_LABELS[p.status] ?? p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {p.method === "havale" && p.status === "pending" ? (
                        <button
                          type="button"
                          onClick={() => setConfirming(p.id)}
                          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                        >
                          <i className="ri-check-line" aria-hidden="true"></i>
                          Onayla
                        </button>
                      ) : (
                        <span className="text-foreground-400 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        open={!!confirming}
        onClose={() => setConfirming(null)}
        title="Ödemeyi Onayla"
        titleId="confirm-payment-title"
        id="confirm-payment-modal"
        maxWidthClass="max-w-sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-foreground-600">
            Bu havale ödemesini onaylayıp ilgili aboneliği aktifleştirmek istediğine emin misin?
          </p>
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setConfirming(null)}
              className="h-10 px-4 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={busy}
              className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap"
            >
              {busy ? "Onaylanıyor..." : "Onayla"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}